import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight, Sparkles } from "lucide-react";

export const Route = createFileRoute("/demo/norda-living")({
  component: NordaLivingDemo,
  head: () => ({
    meta: [
      { title: "NORDA Living — Mobili d'autore per la casa" },
      {
        name: "description",
        content:
          "NORDA Living: arredo su misura, materiali nobili e design senza tempo. Atelier di mobili premium per la casa.",
      },
    ],
  }),
});

const FONT_STACK = "'Helvetica Neue', Helvetica, Arial, sans-serif";

function HeroBadge() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-md border border-white/40 mx-auto mb-4 w-fit"
    >
      <Sparkles className="w-4 h-4 text-[#5E6470]" />
      <span className="text-xs font-normal text-[#5E6470] tracking-wide uppercase">
        Collezione 2026
      </span>
    </motion.div>
  );
}

function Navbar() {
  const items = [
    { label: "Collezioni", hasDropdown: true },
    { label: "Materiali", hasDropdown: false },
    { label: "Progetti", hasDropdown: false },
    { label: "Atelier", hasDropdown: true },
  ];
  return (
    <nav className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 md:px-8 py-5">
      <div className="hidden md:block w-32" />
      <ul className="hidden md:flex items-center gap-7 px-6 py-2.5 rounded-full bg-white/50 backdrop-blur-xl border border-white/40 text-[13px] font-normal text-[#1e325a]">
        {items.map((it) => (
          <li
            key={it.label}
            className="cursor-pointer hover:opacity-70 transition-opacity flex items-center gap-1 group"
          >
            {it.label}
            {it.hasDropdown && (
              <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            )}
          </li>
        ))}
      </ul>
      <div className="md:hidden text-[#1e325a] font-semibold tracking-[0.2em] text-sm">
        NORDA
      </div>
      <div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center bg-[rgba(30,50,90,0.85)] text-white rounded-full pl-2 pr-4 md:pr-6 py-1.5 md:py-2 gap-2 md:gap-3 hover:bg-[rgba(30,50,90,1)] transition-colors group"
        >
          <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/15 flex items-center justify-center">
            <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </span>
          <span className="text-xs md:text-sm font-normal">Prenota visita</span>
        </motion.button>
      </div>
    </nav>
  );
}

function BottomLeftCard() {
  return (
    <motion.div
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="absolute bottom-28 right-4 left-auto md:left-6 md:right-auto md:bottom-6 lg:bottom-10 lg:left-10 p-3 md:p-4 lg:p-5 rounded-[1.2rem] md:rounded-[1.5rem] lg:rounded-[2.2rem] bg-white/40 backdrop-blur-xl border border-white/40 flex flex-col gap-2 lg:gap-3 min-w-[150px] md:min-w-[160px] lg:min-w-[190px] w-fit"
    >
      <div className="flex flex-col">
        <span className="text-2xl md:text-3xl font-normal text-[rgba(30,50,90,0.9)] tracking-tight">
          1.200+
        </span>
        <span className="text-[10px] md:text-[12px] font-normal text-[rgba(30,50,90,0.6)] uppercase tracking-wider">
          Progetti realizzati
        </span>
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="flex items-center bg-white rounded-full pl-1.5 pr-5 py-1.5 gap-2 hover:bg-white/90 transition-colors self-start group"
      >
        <span className="w-7 h-7 rounded-full bg-[rgba(30,50,90,0.06)] flex items-center justify-center">
          <ArrowUpRight className="w-4 h-4 text-[rgba(30,50,90,0.9)]" />
        </span>
        <span className="text-[13px] font-normal text-[rgba(30,50,90,0.9)]">
          Visita lo showroom
        </span>
      </motion.button>
    </motion.div>
  );
}

function BottomRightCorner() {
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.4 }}
      className="absolute bottom-0 right-0 p-3 pt-5 pl-8 sm:p-4 sm:pt-6 sm:pl-10 md:p-6 md:pt-8 md:pl-14 bg-[#f0f0f0] rounded-tl-[1.5rem] sm:rounded-tl-[2rem] md:rounded-tl-[3.5rem] flex items-center gap-3 sm:gap-4 md:gap-6"
    >
      {/* Corner cutout masks */}
      <span className="absolute -top-5 right-0 w-5 h-5 bg-transparent shadow-[5px_-5px_0_0_#f0f0f0] rounded-br-[1rem]" />
      <span className="absolute bottom-0 -left-5 w-5 h-5 bg-transparent shadow-[5px_5px_0_0_#f0f0f0] rounded-br-[1rem]" />

      <div className="bg-[rgba(30,50,90,0.05)] w-10 h-10 md:w-14 md:h-14 rounded-full flex items-center justify-center border border-[rgba(30,50,90,0.1)]">
        <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 text-[rgba(30,50,90,0.8)]" />
      </div>
      <div className="flex flex-col">
        <span className="text-[16px] md:text-[20px] font-normal text-[rgba(30,50,90,0.95)] leading-tight">
          Catalogo 2026
        </span>
        <span className="flex items-center gap-1 text-[12px] md:text-[13px] text-[rgba(30,50,90,0.6)]">
          Sfoglia la collezione
          <ChevronRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </motion.div>
  );
}

function Hero() {
  return (
    <section
      className="w-full min-h-screen bg-[#f0f0f0] p-2 sm:p-3 md:p-4"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="relative w-full h-[calc(100vh-1rem)] sm:h-[calc(100vh-1.5rem)] md:h-[calc(100vh-2rem)] rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover object-[65%] lg:object-center z-0"
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260428_193507_4286c423-2fd9-4efd-92bd-91a939453fc1.mp4"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/30 z-[1]" />

        <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 text-center">
          <Navbar />
          <div className="flex flex-col items-center max-w-3xl pt-16">
            <HeroBadge />
            <motion.h1
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-[80px] font-normal text-white mb-3 tracking-tight leading-[1.05]"
              style={{ textShadow: "0 2px 30px rgba(0,0,0,0.5)" }}
            >
              Mobili d'autore
              <br />
              per la tua casa
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-sm sm:text-base md:text-lg text-white opacity-95 leading-relaxed max-w-xl font-normal"
              style={{ textShadow: "0 1px 20px rgba(0,0,0,0.5)" }}
            >
              Arredo su misura, materiali nobili e design senza tempo. Atelier
              artigianale tra Lugano e Como per progetti residenziali esclusivi.
            </motion.p>
          </div>
          <BottomLeftCard />
          <BottomRightCorner />
        </div>
      </div>
    </section>
  );
}

function Collections() {
  const items = [
    {
      name: "Living",
      desc: "Divani componibili, poltrone scolpite, tavoli in legno massello.",
    },
    {
      name: "Dining",
      desc: "Tavoli in marmo e rovere, sedute imbottite, credenze su misura.",
    },
    {
      name: "Notte",
      desc: "Letti boiserie, comodini sospesi, armadiature integrate.",
    },
    {
      name: "Outdoor",
      desc: "Arredo da giardino in teak, lounge per terrazze e ville al lago.",
    },
  ];
  return (
    <section
      className="bg-[#f0f0f0] px-4 md:px-10 py-20 md:py-28"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[rgba(30,50,90,0.5)]">
              Collezioni
            </span>
            <h2 className="text-3xl md:text-5xl text-[#1e325a] mt-2 tracking-tight leading-[1.05]">
              Quattro mondi,
              <br />
              un'unica firma.
            </h2>
          </div>
          <p className="text-[rgba(30,50,90,0.7)] max-w-md text-sm md:text-base">
            Ogni collezione nasce nel nostro atelier e viene realizzata da
            maestri artigiani italiani con materiali certificati e tempi di
            lavorazione su misura.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div
              key={it.name}
              className="rounded-2xl bg-white border border-black/5 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="w-10 h-10 rounded-full bg-[rgba(30,50,90,0.06)] flex items-center justify-center mb-5">
                <ArrowUpRight className="w-4 h-4 text-[rgba(30,50,90,0.8)]" />
              </div>
              <h3 className="text-xl text-[#1e325a] mb-2">{it.name}</h3>
              <p className="text-sm text-[rgba(30,50,90,0.7)] leading-relaxed">
                {it.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Press() {
  return (
    <section
      className="bg-[#f0f0f0] px-4 md:px-10 pb-4 pt-8"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="max-w-6xl mx-auto rounded-2xl bg-white border border-black/5 px-6 py-8 md:px-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[rgba(30,50,90,0.5)] text-center mb-5">
          Selezionati e raccontati da
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[#1e325a]/70">
          <span className="text-base md:text-lg italic">Elle Decor</span>
          <span className="text-black/15">·</span>
          <span className="text-base md:text-lg">AD Italia</span>
          <span className="text-black/15">·</span>
          <span className="text-base md:text-lg italic">Living Corriere</span>
          <span className="text-black/15">·</span>
          <span className="text-base md:text-lg">Domus</span>
          <span className="text-black/15">·</span>
          <span className="text-base md:text-lg italic">Cose di Casa</span>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    { n: "1.200+", l: "Progetti residenziali" },
    { n: "22", l: "Anni di atelier" },
    { n: "14", l: "Maestri artigiani" },
    { n: "100%", l: "Legni FSC certificati" },
  ];
  return (
    <section
      className="bg-[#f0f0f0] px-4 md:px-10 py-16"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((s) => (
          <div key={s.l} className="text-center md:text-left">
            <div className="text-4xl md:text-5xl text-[#1e325a] tracking-tight">{s.n}</div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.25em] text-[rgba(30,50,90,0.55)]">{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const items = [
    {
      q: "Hanno seguito ogni dettaglio: dal rilievo in villa alla consegna finale. Il salotto su misura è esattamente come l'avevamo immaginato.",
      n: "Famiglia Bernasconi",
      r: "Villa al Lago · Morcote",
    },
    {
      q: "Per la nostra suite presidenziale abbiamo scelto NORDA. Qualità dei materiali e finiture al livello dei migliori atelier milanesi.",
      n: "Arch. Laura Conti",
      r: "Boutique Hotel · Como",
    },
    {
      q: "Tempi rispettati, prezzi trasparenti e un team che ascolta davvero. Tornerei a chiamarli per ogni nuovo progetto.",
      n: "Marco e Sara V.",
      r: "Attico ristrutturato · Lugano",
    },
  ];
  return (
    <section
      className="bg-[#f0f0f0] px-4 md:px-10 py-20 md:py-24"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="mb-12 max-w-2xl">
          <span className="text-xs uppercase tracking-[0.25em] text-[rgba(30,50,90,0.5)]">Clienti</span>
          <h2 className="text-3xl md:text-5xl text-[#1e325a] mt-2 tracking-tight leading-[1.05]">
            Famiglie, architetti e hotel <br /> ci scelgono dal 2003.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <figure
              key={i}
              className="rounded-2xl bg-white border border-black/5 p-7 flex flex-col justify-between"
            >
              <blockquote className="text-[15px] leading-relaxed text-[rgba(30,50,90,0.85)]">
                "{t.q}"
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-black/5">
                <div className="text-sm text-[#1e325a]">{t.n}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[rgba(30,50,90,0.5)] mt-1">
                  {t.r}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const items = [
    { q: "Quanto tempo serve per realizzare un progetto su misura?", a: "Dipende dalla complessità: un ambiente singolo richiede in media 8–10 settimane, una casa completa 4–6 mesi. Durante il sopralluogo definiamo timeline e milestone." },
    { q: "Lavorate anche fuori dal Ticino?", a: "Sì: progettiamo e consegniamo in tutta la Svizzera, Lombardia e Costa Azzurra. Il sopralluogo iniziale è sempre incluso entro 100 km dall'atelier." },
    { q: "Come funziona il preventivo?", a: "Primo sopralluogo gratuito, preventivo dettagliato con rendering 3D entro 10 giorni. Acconto del 30% alla conferma, saldo alla consegna." },
    { q: "Che garanzia date sui mobili?", a: "10 anni sulla struttura portante, 5 anni sui meccanismi e rivestimenti. Servizio di manutenzione e ripristino disponibile anche dopo la garanzia." },
  ];
  return (
    <section
      className="bg-[#f0f0f0] px-4 md:px-10 py-20"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[rgba(30,50,90,0.5)]">Domande frequenti</span>
          <h2 className="text-3xl md:text-5xl text-[#1e325a] mt-2 tracking-tight leading-[1.05]">
            Tutto quello che serve sapere.
          </h2>
        </div>
        <div className="divide-y divide-black/10 border-y border-black/10">
          {items.map((f, i) => (
            <details key={i} className="group py-5">
              <summary className="flex justify-between items-center cursor-pointer list-none">
                <span className="text-lg text-[#1e325a] pr-6">{f.q}</span>
                <span className="text-[#1e325a] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-[rgba(30,50,90,0.7)] leading-relaxed text-sm">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function NordaLivingDemo() {
  return (
    <main className="bg-[#f0f0f0] min-h-screen">
      <Hero />
      <Collections />
      <Press />
      <Stats />
      <Testimonials />
      <Faq />
      <footer
        className="bg-[#f0f0f0] px-4 md:px-10 py-10 text-center text-xs text-[rgba(30,50,90,0.5)]"
        style={{ fontFamily: FONT_STACK }}
      >
        © 2026 NORDA Living — Atelier di arredo, Lugano · Como. Demo realizzata
        da FlashWeb. Iscritta CCIA Como n. 0023145 · IVA CH-501.234.567 MWST.
      </footer>
    </main>
  );
}

